import React, { useRef, useState, useLayoutEffect } from "react";
import { TimelineGraphMonthHeader } from "./timelineGraph/TimelineGraphMonthHeader";
import { TimelineGraphEvent, TimelineGraphYearRow } from "./timelineGraph/TimelineGraphYearRow";
import * as dateMath from 'date-arithmetic';
import { Flex, FlexItem } from "../compositeComponents/FlexHelpers";
import { Button } from "../button/Button";
import { H2 } from "../typography/Heading";
import styled from "styled-components";

export type TimelineGraphEventGroup = {
    legend: { text: string, color: string; },
    events: TimelineGraphEvent[];
};

type TimelineGraphProps = {
    headerText?: string,
    rowHeightPx: number,
    yearMonthStart: Date,
    eventGroups: TimelineGraphEventGroup[],
    onNavigate?: (yearMonthStart: Date) => void;
    periodFormat?: 'start' | 'to-from';
};
export function TimelineGraph (props: TimelineGraphProps): JSX.Element
{

    function getStartDateString ()
    {

        const month = props.yearMonthStart.getMonth() + 1;
        const monthStr = month < 10
            ? `0${month}`
            : `${month}`;
        return `${monthStr} - ${props.yearMonthStart.getFullYear()}`;
    }

    async function handleClickNavigate (months: number)
    {
        const newStart = dateMath.add(
            new Date(
                props.yearMonthStart.getFullYear(),
                props.yearMonthStart.getMonth(),
                1,
                0,
                0,
                0
            ),
            months,
            'month',
        );
        props.onNavigate?.(newStart);
    }

    return (
        <Flex flexDirection="column" gap={24}>
            <Flex>
                {props.headerText &&
                    <H2>{props.headerText}</H2>
                }

                <FlexItem flexGrow="1">
                    {props.onNavigate &&
                        <Flex flexDirection="row" justifyContent="center" alignItems="center">
                            <Button variant="text" icon={"keyboard_double_arrowLeft"} onClick={() => handleClickNavigate(-12)} />
                            <Button variant="text" icon={"arrows_chevron_chevronBack"} onClick={() => handleClickNavigate(-1)} />
                            <StartDateLabel>{getStartDateString()}</StartDateLabel>
                            <Button variant="text" icon={"arrows_chevron_chevronForward"} onClick={() => handleClickNavigate(1)} />
                            <Button variant="text" icon={"keyboard_double_arrowRight"} onClick={() => handleClickNavigate(12)} />
                        </Flex>
                    }
                </FlexItem>
            </Flex>
            
            <InternalTimelineGraph {...props} periodFormat={props.periodFormat ?? 'start'} />
            
        </Flex>
    );
}

const StartDateLabel = styled.label`
    padding: 10px 20px;
    font-size: 18px;
    font-weight: 700;
    color: rgb(85, 105, 139);
    text-transform: uppercase;
`;


type InternalTimelineGraphProps = {
    rowHeightPx: number,
    yearMonthStart: Date,
    eventGroups: {
        legend: { text: string, color: string; },
        events: TimelineGraphEvent[];
    }[],
    periodFormat: 'start' | 'to-from';
};
function InternalTimelineGraph (props: InternalTimelineGraphProps): JSX.Element
{

    const ref = useRef(null);
    const [graphWidthPx, setGraphWidthPx] = useState(0);

    useLayoutEffect(() =>
    {
        if (ref.current)
        {
            setGraphWidthPx(ref.current.offsetWidth);
        }
    }, []);

    useLayoutEffect(() =>
    {
        function handleResize ()
        {
            if (ref.current)
            {
                setGraphWidthPx(ref.current.offsetWidth);
            }
        }

        window.addEventListener('resize', handleResize);
    });

    let nowOffsetPx: number | undefined = undefined;
    const graphStartMs = props.yearMonthStart.getTime();
    const graphEndMs = dateMath.add(props.yearMonthStart, 1, 'year').getTime() - 1;
    const nowMs = Date.now();
    if (nowMs >= graphStartMs && nowMs <= graphEndMs)
    {
        const nowInGraphScale = (nowMs - graphStartMs) / (graphEndMs - graphStartMs);
        nowOffsetPx = nowInGraphScale * graphWidthPx;
    }

    return (
        <div style={{ display: 'flex' }}>
            <div ref={ref} style={{ flexGrow: 1 }}>

                <TimelineGraphMonthHeader yearMonthStart={props.yearMonthStart} rowWidthPx={graphWidthPx} months={12} />

                {nowOffsetPx &&
                    <div style={{
                        position: 'absolute',
                        marginLeft: nowOffsetPx - 1,
                        width: 0,
                        height: (props.rowHeightPx + 1 + 16) * props.eventGroups.length,
                        borderLeft: '3px solid black',
                        zIndex: 2
                    }}></div>
                }

                <div className="fluh">
                    {props.eventGroups.map((eg, i) =>
                    {
                        return (
                            <TimelineGraphYearRow key={eg.legend.text}
                                firstRow={i === 0}
                                graphWidthPx={graphWidthPx}
                                yearMonthStart={props.yearMonthStart}
                                rowHeightPx={props.rowHeightPx}
                                activities={eg.events}
                                color={eg.legend.color} 
                                eventGroupsTitle={eg.legend.text}
                                />
                        );
                    })}
                </div>
            </div>
        </div>
    );
}